import {
  SOUNDSCAPE_BY_ID,
  type SoundscapeCategoryId,
  type SoundscapeId,
  type SoundscapeTrackState,
} from './catalog'

const CATEGORY_LEVELS: Record<SoundscapeCategoryId, number> = {
  nature: .5, rain: .4, animals: .36, urban: .34, places: .34, transport: .34, things: .3,
  noise: 0.34,
}

export class SoundscapeEngine {
  private tracks = new Map<SoundscapeId, HTMLAudioElement>()
  private playing = false

  private createTrack(id: SoundscapeId) {
    const definition = SOUNDSCAPE_BY_ID.get(id)
    if (!definition) throw new Error(`未知声景：${id}`)
    const audio = new Audio(definition.audioUrl)
    audio.loop = true
    audio.preload = 'auto'
    return audio
  }

  private ensureTracks(tracks: SoundscapeTrackState[]) {
    const active = new Set(tracks.map(track => track.id))
    for (const [id, audio] of this.tracks) {
      if (active.has(id)) continue
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      this.tracks.delete(id)
    }
    for (const track of tracks) {
      let audio = this.tracks.get(track.id)
      if (!audio) {
        audio = this.createTrack(track.id)
        this.tracks.set(track.id, audio)
        if (this.playing) void audio.play().catch(() => undefined)
      }
      this.setVolume(track.id, track.volume)
    }
  }

  async play(tracks: SoundscapeTrackState[]) {
    this.ensureTracks(tracks)
    this.playing = true
    try {
      await Promise.all([...this.tracks.values()].map(audio => audio.play()))
    } catch (error) {
      this.pause()
      throw new Error('高品质在线声景加载失败，请检查网络后重试', { cause: error })
    }
  }

  pause() {
    this.playing = false
    for (const audio of this.tracks.values()) audio.pause()
  }

  setTracks(tracks: SoundscapeTrackState[]) {
    this.ensureTracks(tracks)
  }

  setVolume(id: SoundscapeId, volume: number) {
    const audio = this.tracks.get(id)
    if (!audio) return
    const definition = SOUNDSCAPE_BY_ID.get(id)
    audio.volume = Math.max(0, Math.min(1, volume)) * (definition ? CATEGORY_LEVELS[definition.category] : 0.38)
  }

  destroy() {
    this.playing = false
    for (const audio of this.tracks.values()) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    this.tracks.clear()
  }
}
